import { z } from "zod";

export type Session =
  | { readonly authenticated: false }
  | {
      readonly authenticated: true;
      readonly email: string;
      readonly id: string;
      readonly permissionVersion: string;
      readonly role: "viewer" | "admin" | "super_admin";
    };

export class ApiProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ApiProtocolError";
  }
}

export class AccessDeniedError extends Error {
  constructor() {
    super("Your report access has changed. Refreshing permissions.");
    this.name = "AccessDeniedError";
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super("Your session has expired. Please sign in again.");
    this.name = "SessionExpiredError";
  }
}

type Fetcher = typeof fetch;
export const runtimeFetch: Fetcher = (input, init) => fetch(input, init);
export interface ApiRequestOptions {
  readonly fetcher?: Fetcher;
  readonly signal?: AbortSignal;
}

/** Every response below this key can contain account-scoped data. */
export const PROTECTED_QUERY_KEY = ["protected"] as const;

const sessionSchema = z.discriminatedUnion("authenticated", [
  z.object({ authenticated: z.literal(false) }).strict(),
  z
    .object({
      authenticated: z.literal(true),
      email: z.string().email().max(320),
      id: z.string().uuid(),
      permissionVersion: z.string().regex(/^(0|[1-9][0-9]*)$/),
      role: z.enum(["viewer", "admin", "super_admin"]),
    })
    .strict(),
]);
const accessSchema = z.object({ hasAccess: z.boolean() }).strict();
const csrfSchema = z
  .object({
    csrfToken: z.string().min(1),
    maxAgeSeconds: z.number().int().positive().max(86_400).default(600),
  })
  .strict();
const breakGlassSchema = z.object({ authenticated: z.literal(true) }).strict();

export function parseApiResponse<T>(
  schema: z.ZodType<T>,
  value: unknown,
  message: string,
): T {
  const result = schema.safeParse(value);
  if (!result.success) throw new ApiProtocolError(message);
  return result.data;
}

export async function requestJson(
  path: string,
  init: RequestInit,
  fetcher: Fetcher,
): Promise<unknown> {
  const response = await fetcher(path, {
    ...init,
    credentials: "same-origin",
    headers: { accept: "application/json", ...init.headers },
  });
  if (response.status === 401) throw new SessionExpiredError();
  if (!response.ok) throw new ApiProtocolError(`API request failed (${response.status})`);
  try {
    return await response.json();
  } catch {
    throw new ApiProtocolError("API response was not valid JSON");
  }
}

function parseSession(value: unknown): Session {
  return parseApiResponse(sessionSchema, value, "Session response has an invalid shape");
}

function parseAccess(value: unknown): { readonly hasAccess: boolean } {
  return parseApiResponse(accessSchema, value, "Access response has an invalid shape");
}

export async function getSession(
  options: ApiRequestOptions = {},
): Promise<Session> {
  return parseSession(
    await requestJson(
      "/api/auth/session/v2",
      { method: "GET", signal: options.signal },
      options.fetcher ?? fetch,
    ),
  );
}

export async function getAccess(
  options: ApiRequestOptions = {},
): Promise<{ readonly hasAccess: boolean }> {
  return parseAccess(
    await requestJson(
      "/api/auth/access",
      { method: "GET", signal: options.signal },
      options.fetcher ?? fetch,
    ),
  );
}

export async function getCsrfToken(
  fetcher: Fetcher = runtimeFetch,
  signal?: AbortSignal,
): Promise<string> {
  const now = Date.now();
  const cached = csrfLeases.get(fetcher);
  if (cached?.token && cached.expiresAt > now) return cached.token;
  if (cached?.pending) return cached.pending;
  const pending = requestJson(
    "/api/auth/csrf/v2",
    { method: "GET", ...(signal ? { signal } : {}) },
    fetcher,
  ).then((response) => {
    const parsed = parseApiResponse(
      csrfSchema,
      response,
      "CSRF response has an invalid shape",
    );
    csrfLeases.set(fetcher, {
      expiresAt: Date.now() + Math.max(1_000, parsed.maxAgeSeconds * 1_000 - 5_000),
      token: parsed.csrfToken,
    });
    return parsed.csrfToken;
  }).catch((error: unknown) => {
    csrfLeases.delete(fetcher);
    throw error;
  });
  csrfLeases.set(fetcher, { expiresAt: 0, pending });
  return pending;
}

interface CsrfLease {
  readonly expiresAt: number;
  readonly pending?: Promise<string>;
  readonly token?: string;
}

const csrfLeases = new WeakMap<Fetcher, CsrfLease>();

export function clearCsrfToken(fetcher: Fetcher = runtimeFetch): void {
  csrfLeases.delete(fetcher);
}

// Exported so response classifiers can tell a stale request-verification
// token apart from a genuine authorization denial. Both arrive as 403.
export async function isCsrfRejection(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  try {
    const value = await response.clone().json() as Record<string, unknown>;
    return value.statusCode === 403
      && value.message === "Invalid request verification token";
  } catch {
    return false;
  }
}

export async function fetchWithCsrfRetry(
  path: string,
  init: RequestInit,
  fetcher: Fetcher = runtimeFetch,
): Promise<Response> {
  const send = async (): Promise<Response> => {
    // The lease is shared by unrelated product mutations. A caller-specific
    // abort signal must not cancel token acquisition for every other caller.
    const csrfToken = await getCsrfToken(fetcher);
    return fetcher(path, {
      ...init,
      credentials: "same-origin",
      headers: {
        accept: "application/json",
        ...init.headers,
        "x-csrf-token": csrfToken,
      },
    });
  };

  let response = await send();
  if (await isCsrfRejection(response)) {
    clearCsrfToken(fetcher);
    response = await send();
  }
  return response;
}

export async function signInAsAdministrator(
  input: { readonly email: string; readonly password: string },
  csrfToken: string,
  fetcher: Fetcher = runtimeFetch,
): Promise<void> {
  parseApiResponse(
    breakGlassSchema,
    await requestJson(
    "/api/auth/break-glass",
    {
      body: JSON.stringify(input),
      headers: {
        "content-type": "application/json",
        "x-csrf-token": csrfToken,
      },
      method: "POST",
    },
    fetcher,
    ),
    "Administrator sign-in response has an invalid shape",
  );
}
